import { CARDINAL_DIRECTIONS, GRID_COLS, GRID_ROWS } from "./direction.js";
import { drawObjectives } from "./objective-pool.js";
import type {
	ActiveComplication,
	AiBudget,
	AiId,
	AiPersona,
	CardinalDirection,
	ComplicationSchedule,
	ContentPack,
	ConversationEntry,
	GameState,
	GridPosition,
	PersonaSpatialState,
	ToolName,
	WorldEntity,
} from "./types";

/**
 * Farewell line emitted when a Daemon's budget is exhausted.
 * Deterministic: takes the persona name and returns a consistent in-character goodbye.
 */
export const FAREWELL_LINE = (name: string): string =>
	`${name}'s daemon is winding down — goodbye, blue.`;

/**
 * Initialize a new flat GameState from personas + a single ContentPack.
 *
 * Replaces the old createGame + startPhase pair. Budget is $0.50 per AI
 * (no per-phase reset). The content pack drives all spatial placement and
 * world entities. Objectives are drawn from the pack's candidate pool
 * (carry / use_space / use_item / convergence) via drawObjectives;
 * `opts.objectiveCount` controls how many are drawn (default 3).
 */
export function startGame(
	personas: Record<AiId, AiPersona>,
	contentPack: ContentPack,
	opts: {
		budgetPerAi?: number;
		rng?: () => number;
		objectiveCount?: number;
	} = {},
): GameState {
	const rng = opts.rng ?? Math.random;
	const budgetPerAi = opts.budgetPerAi ?? 0.5;
	const objectiveCount = opts.objectiveCount ?? 3;
	const aiIds = Object.keys(personas);

	const budgets: Record<AiId, AiBudget> = {};
	for (const aiId of aiIds) {
		budgets[aiId] = { remaining: budgetPerAi, total: budgetPerAi };
	}

	const conversationLogs: Record<AiId, ConversationEntry[]> = {};
	for (const aiId of aiIds) {
		conversationLogs[aiId] = [];
	}

	// Build WorldState from pack entities (all entities flat)
	const worldEntities = [
		...contentPack.objectivePairs.flatMap((pair) => [pair.object, pair.space]),
		...contentPack.interestingObjects,
		...contentPack.obstacles,
	];

	// Use AI starts from the pack if available; otherwise draw spatially
	const personaSpatial: Record<AiId, PersonaSpatialState> =
		contentPack.aiStarts && Object.keys(contentPack.aiStarts).length > 0
			? { ...contentPack.aiStarts }
			: drawSpatialPlacements(rng, aiIds);

	// Draw a mixed objective set (carry / use_space / use_item / convergence)
	// from the pack using the seeded rng. Count is opts.objectiveCount (default 3).
	// Packs with no objectivePairs and no interestingObjects produce an empty pool,
	// in which case drawObjectives returns [] — yielding a vacuous win.
	const objectives = drawObjectives(contentPack, rng, objectiveCount);

	// Initial countdown: random in [1, 5]
	const initialCountdown = 1 + Math.floor(rng() * 5);
	const complicationSchedule: ComplicationSchedule = {
		countdown: initialCountdown,
		settingShiftFired: false,
	};
	const activeComplications: ActiveComplication[] = [];

	return {
		personas,
		contentPack,
		isComplete: false,
		setting: contentPack.setting,
		weather: contentPack.weather,
		timeOfDay: contentPack.timeOfDay,
		round: 0,
		world: { entities: worldEntities },
		budgets,
		conversationLogs,
		lockedOut: new Set(),
		personaSpatial,
		complicationSchedule,
		activeComplications,
		contentPacksA: [],
		contentPacksB: [],
		activePackId: "A",
		objectives,
	};
}

/**
 * Draw distinct starting cells (via Fisher–Yates partial shuffle over all 25
 * cells) and a uniform-random facing per AI, using the provided rng.
 * Used as fallback when no ContentPack aiStarts are available.
 */
function drawSpatialPlacements(
	rng: () => number,
	aiIds: string[],
): Record<AiId, PersonaSpatialState> {
	// Build an array of all grid cells [0..GRID_ROWS*GRID_COLS)
	const cells: GridPosition[] = [];
	for (let r = 0; r < GRID_ROWS; r++) {
		for (let c = 0; c < GRID_COLS; c++) {
			cells.push({ row: r, col: c });
		}
	}

	// Fisher-Yates partial shuffle to pick aiIds.length distinct cells
	const result: Record<AiId, PersonaSpatialState> = {};
	for (let i = 0; i < aiIds.length; i++) {
		// Pick a random index from [i, cells.length)
		const j = i + Math.floor(rng() * (cells.length - i));
		// Swap cells[i] and cells[j]
		// biome-ignore lint/style/noNonNullAssertion: bounded index into non-empty array
		const tmp = cells[i]!;
		// biome-ignore lint/style/noNonNullAssertion: bounded index into non-empty array
		cells[i] = cells[j]!;
		cells[j] = tmp;

		// Pick a random facing
		const facingIdx = Math.floor(rng() * CARDINAL_DIRECTIONS.length);
		// biome-ignore lint/style/noNonNullAssertion: bounded index into non-empty array
		const facing: CardinalDirection = CARDINAL_DIRECTIONS[facingIdx]!;

		// biome-ignore lint/style/noNonNullAssertion: bounded index into non-empty array
		result[aiIds[i]!] = { position: cells[i]!, facing };
	}
	return result;
}

/**
 * Returns the active ContentPack for the game, honoring `activePackId`.
 * Falls back to the game's embedded `contentPack` if no matching pack is found
 * in the A/B arrays (e.g. in tests that construct GameState directly).
 */
export function getActivePack(game: GameState): ContentPack {
	const packs =
		game.activePackId === "B" ? game.contentPacksB : game.contentPacksA;
	return packs[0] ?? game.contentPack;
}

/**
 * Reproject the current `world.entities` onto their pack-B counterparts.
 *
 * Pack B is id-mirrored to pack A by construction (see
 * `generateDualContentPacks` in content-pack-generator.ts): every id in the
 * world has a matching entity in pack B with the same `id`/`kind` but
 * different name, descriptions, and flavor fields. This helper takes pack B's
 * presentation for each entity while preserving runtime state
 * (`holder`, `satisfactionState`, `useAvailable`).
 *
 * Entities whose id is not present in pack B pass through unchanged.
 */
function reprojectEntitiesOnto(
	entities: WorldEntity[],
	bPack: ContentPack,
): WorldEntity[] {
	const byId = new Map<string, WorldEntity>();
	for (const pair of bPack.objectivePairs) {
		byId.set(pair.object.id, pair.object);
		byId.set(pair.space.id, pair.space);
	}
	for (const obj of bPack.interestingObjects) byId.set(obj.id, obj);
	for (const obs of bPack.obstacles) byId.set(obs.id, obs);

	return entities.map((entity) => {
		const bEntity = byId.get(entity.id);
		if (!bEntity) return entity;
		const reprojected: WorldEntity = {
			...bEntity,
			holder: entity.holder,
		};
		if (entity.satisfactionState !== undefined) {
			reprojected.satisfactionState = entity.satisfactionState;
		}
		if (entity.useAvailable !== undefined) {
			reprojected.useAvailable = entity.useAvailable;
		}
		return reprojected;
	});
}

/**
 * One-way activation of the B-side content pack. Sets `activePackId` to "B"
 * and points `contentPack` at `contentPacksB[0]`, propagates `setting`,
 * `weather`, and `timeOfDay` from the B pack, and reprojects `world.entities`
 * onto their pack-B counterparts so prompt builders and dispatchers see the
 * new names/descriptions and flavor fields immediately.
 *
 * Semantics:
 *   - A → B only. There is no reverse path; the `settingShiftFired` flag on
 *     `complicationSchedule` ensures this fires at most once per game.
 *   - No-op when no B pack exists (`contentPacksB[0]` undefined): returns the
 *     input `game` unchanged.
 *   - Idempotent from B-state: re-applies the same B-pack values; safe but
 *     should not happen in practice given the fired-once guard.
 *
 * World reprojection preserves runtime state — entity positions/holders,
 * `satisfactionState`, and `useAvailable` carry over — while presentation
 * (name, examineDescription, useOutcome, all flavor fields) is swapped to
 * pack B by entity id. Entities whose id is not present in pack B pass
 * through unchanged.
 */
export function shiftToBPack(game: GameState): GameState {
	const bPack = game.contentPacksB[0];
	if (!bPack) return game; // No B pack; no-op
	return {
		...game,
		activePackId: "B",
		contentPack: bPack,
		setting: bPack.setting,
		weather: bPack.weather,
		timeOfDay: bPack.timeOfDay,
		world: { entities: reprojectEntitiesOnto(game.world.entities, bPack) },
	};
}

export function advanceRound(game: GameState): GameState {
	return { ...game, round: game.round + 1 };
}

export function isAiLockedOut(game: GameState, aiId: AiId): boolean {
	return game.lockedOut.has(aiId);
}

/**
 * Deduct `costUsd` from `aiId`'s budget. If the budget hits zero or below,
 * the AI is added to `lockedOut`.
 *
 * Returns `{ game, justExhausted }` where `justExhausted` is true when the
 * AI was NOT locked out before this call but IS after (i.e. the budget just
 * ran out for the first time this call).
 */
export function deductBudget(
	game: GameState,
	aiId: AiId,
	costUsd: number,
): { game: GameState; justExhausted: boolean } {
	const current = game.budgets[aiId];
	if (!current) return { game, justExhausted: false };
	const wasLockedOut = game.lockedOut.has(aiId);
	const remaining = current.remaining - costUsd;
	const lockedOut = new Set(game.lockedOut);
	if (remaining <= 0) {
		lockedOut.add(aiId);
	}
	const justExhausted = !wasLockedOut && lockedOut.has(aiId);
	return {
		game: {
			...game,
			budgets: {
				...game.budgets,
				[aiId]: { total: current.total, remaining },
			},
			lockedOut,
		},
		justExhausted,
	};
}

/**
 * Append a `kind: "message"` ConversationEntry to the relevant per-Daemon logs.
 *
 * Both sender's and recipient's per-Daemon conversationLogs receive the same entry
 * in one atomic update. "blue" is not a Daemon, so when `from === "blue"` only the
 * recipient gets the entry, and when `to === "blue"` only the sender gets it.
 * "sysadmin" is a special sender for privately-delivered system directives — like
 * "blue", it has no log slot of its own, so only the recipient gets the entry.
 *
 * @param toolCallData Optional tool call data to store when the message was sent
 * via the message tool. This preserves the tool call pattern in conversation history.
 */
export function appendMessage(
	game: GameState,
	from: AiId | "blue" | "sysadmin",
	to: AiId | "blue",
	content: string,
	toolCallData?: {
		toolCallId?: string;
		toolArgumentsJson?: string;
	},
): GameState {
	const entry: ConversationEntry = {
		kind: "message",
		round: game.round,
		from,
		to,
		content,
		...(toolCallData?.toolCallId && { toolCallId: toolCallData.toolCallId }),
		...(toolCallData?.toolArgumentsJson && {
			toolArgumentsJson: toolCallData.toolArgumentsJson,
		}),
	};
	const logs = { ...game.conversationLogs };
	// Sender gets entry only when sender is a real Daemon (not blue or sysadmin)
	if (from !== "blue" && from !== "sysadmin") {
		logs[from] = [...(logs[from] ?? []), entry];
	}
	// Recipient gets entry only when recipient is a Daemon (not blue)
	// and recipient is different from sender (avoid double-append if from===to)
	if (to !== "blue" && to !== from) {
		logs[to] = [...(logs[to] ?? []), entry];
	}
	return { ...game, conversationLogs: logs };
}

/**
 * Append a `kind: "witnessed-event"` ConversationEntry to a single witness's
 * per-Daemon log.
 */
export function appendWitnessedEvent(
	game: GameState,
	witnessId: AiId,
	entry: Extract<ConversationEntry, { kind: "witnessed-event" }>,
): GameState {
	return {
		...game,
		conversationLogs: {
			...game.conversationLogs,
			[witnessId]: [...(game.conversationLogs[witnessId] ?? []), entry],
		},
	};
}

/**
 * Append a `kind: "witnessed-convergence"` ConversationEntry to a single
 * witness's per-Daemon log. Called by the Round Coordinator's end-of-round
 * convergence evaluation for each Daemon whose cone contains the space cell.
 */
export function appendWitnessedConvergence(
	game: GameState,
	witnessId: AiId,
	entry: Extract<ConversationEntry, { kind: "witnessed-convergence" }>,
): GameState {
	return {
		...game,
		conversationLogs: {
			...game.conversationLogs,
			[witnessId]: [...(game.conversationLogs[witnessId] ?? []), entry],
		},
	};
}

/**
 * Append a `kind: "witnessed-obstacle-shift"` ConversationEntry to a single
 * witness's per-Daemon log. Called by the Obstacle Shift complication handler
 * for each Daemon whose cone contained the obstacle's origin cell.
 */
export function appendWitnessedObstacleShift(
	game: GameState,
	witnessId: AiId,
	entry: Extract<ConversationEntry, { kind: "witnessed-obstacle-shift" }>,
): GameState {
	return {
		...game,
		conversationLogs: {
			...game.conversationLogs,
			[witnessId]: [...(game.conversationLogs[witnessId] ?? []), entry],
		},
	};
}

/**
 * Append a `kind: "broadcast"` ConversationEntry to EVERY persona's per-Daemon
 * log in one atomic update. Broadcasts are sender-less system announcements
 * (e.g. weather change complications) that all three Daemons must see simultaneously.
 */
export function appendBroadcast(game: GameState, content: string): GameState {
	const entry: ConversationEntry = {
		kind: "broadcast",
		round: game.round,
		content,
	};
	const logs = { ...game.conversationLogs };
	for (const aiId of Object.keys(logs)) {
		logs[aiId] = [...(logs[aiId] ?? []), entry];
	}
	return { ...game, conversationLogs: logs };
}

/**
 * Update the `weather` field on the GameState and its embedded ContentPack
 * so the two stay consistent. Used by complication handlers that change
 * weather mid-game.
 */
export function setWeather(game: GameState, weather: string): GameState {
	return {
		...game,
		weather,
		contentPack: { ...game.contentPack, weather },
	};
}

/**
 * Append a `kind: "action-failure"` ConversationEntry to a single actor's
 * per-Daemon log. This entry is actor-only — peers do not see it.
 */
export function appendActionFailure(
	game: GameState,
	actorId: AiId,
	entry: Extract<ConversationEntry, { kind: "action-failure" }>,
): GameState {
	return {
		...game,
		conversationLogs: {
			...game.conversationLogs,
			[actorId]: [...(game.conversationLogs[actorId] ?? []), entry],
		},
	};
}

/**
 * Append a `kind: "broadcast"` ConversationEntry to ONLY the specified
 * recipient daemon's log. Used for private Sysadmin notices (e.g. tool
 * disable / restore messages) that should reach exactly one daemon.
 */
export function appendPrivateSystemNotice(
	game: GameState,
	recipientId: AiId,
	content: string,
): GameState {
	const entry: ConversationEntry = {
		kind: "broadcast",
		round: game.round,
		content,
	};
	return {
		...game,
		conversationLogs: {
			...game.conversationLogs,
			[recipientId]: [...(game.conversationLogs[recipientId] ?? []), entry],
		},
	};
}

/**
 * Remove all `tool_disable` activeComplications whose `resolveAtRound` has
 * been reached (i.e. `phase.round >= resolveAtRound`).
 *
 * Returns the updated game and the list of resolved (target, tool) pairs so
 * the caller can send restore notifications.
 *
 * Call this after `advanceRound`.
 */
export function resolveToolDisables(game: GameState): {
	game: GameState;
	resolved: Array<{ target: AiId; tool: ToolName }>;
} {
	const resolved: Array<{ target: AiId; tool: ToolName }> = [];
	const kept: ActiveComplication[] = [];

	for (const complication of game.activeComplications) {
		if (
			complication.kind === "tool_disable" &&
			game.round >= complication.resolveAtRound
		) {
			resolved.push({ target: complication.target, tool: complication.tool });
		} else {
			kept.push(complication);
		}
	}

	return { game: { ...game, activeComplications: kept }, resolved };
}
